import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { AuthService } from '../../../core/auth/services/auth.service';
import { ROUTES } from '../../../shared/constants';
import { ChildrenDataService } from '../../../shared/services/children-data.service';
import { StorageUploadService } from '../../../shared/services/storage-upload.service';
import { firestoreRetry } from '../../../shared/utils/firestore-retry';
import {
  type ChildDocument,
  type CircleInvitation,
  type OnboardingChildProfileSection,
  type OnboardingFirstMemoryData,
  OnboardingStep,
  STEP_ORDER,
  type UserOnboardingState,
} from '../models/onboarding.model';
import { OnboardingDataAccessService } from './onboarding-data-access.service';
import { LanguageService } from '../../../shared/services/language.service';
import { AnalyticsService } from '../../../core/analytics/analytics.service';

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private readonly dataAccess = inject(OnboardingDataAccessService);
  private readonly authService = inject(AuthService);
  private readonly auth = inject(Auth);
  private readonly router = inject(Router);
  private readonly storageUpload = inject(StorageUploadService);
  private readonly childrenData = inject(ChildrenDataService);
  private readonly language = inject(LanguageService);
  private readonly analytics = inject(AnalyticsService);

  private readonly _state = signal<UserOnboardingState | null>(null);
  private readonly _children = signal<ChildDocument[]>([]);
  private readonly _loading = signal(false);
  private readonly _saving = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly state = this._state.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly saving = this._saving.asReadonly();
  readonly error = this._error.asReadonly();

  readonly currentStep = computed(
    () => this._state()?.currentStep ?? OnboardingStep.WELCOME
  );

  readonly completedSteps = computed(
    () => this._state()?.completedSteps ?? []
  );

  readonly childrenIds = computed(() => this._state()?.childrenIds ?? []);
  readonly children = this._children.asReadonly();

  readonly currentChildId = computed(() => this._state()?.currentChildId);

  /** First child without a first memory (for first-memory step flow) */
  readonly nextChildWithoutMemory = computed(() => {
    const kids = this._children();
    const state = this._state();
    const currentId = state?.currentChildId;
    if (currentId) {
      const hasMemory = kids.find((c) => c.id === currentId)?.firstMemory;
      if (!hasMemory) return kids.find((c) => c.id === currentId) ?? null;
    }
    return kids.find((c) => !c.firstMemory) ?? null;
  });

  readonly primaryChild = computed(() => this._children()[0] ?? null);

  readonly isCompleted = computed(
    () =>
      this._state()?.currentStep === OnboardingStep.COMPLETED ||
      (this._state()?.completedSteps?.includes(OnboardingStep.INVITE_CIRCLE) ??
        false)
  );

  readonly inviteCircle = computed(
    () => this._state()?.inviteCircle?.invitations ?? []
  );

  /** Load onboarding state and children from Firestore */
  async loadOnboarding(): Promise<void> {
    this._loading.set(true);
    this._error.set(null);
    try {
      const { state, children } = await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.loadUserOnboarding(uid)
      );
      this._state.set(state);
      this._children.set(children);
    } catch (e) {
      this._error.set(e instanceof Error ? e.message : 'Error loading onboarding');
      console.error('loadOnboarding failed', e);
    } finally {
      this._loading.set(false);
    }
  }

  /** Initialize onboarding for a new user */
  async initOnboarding(): Promise<void> {
    this._saving.set(true);
    this._error.set(null);
    try {
      const user = this.authService.user();
      await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.ensureUserDoc(uid, {
          email: user?.email ?? '',
          displayName: user?.displayName ?? '',
          photoURL: user?.photoURL ?? null,
          preferredLanguage: this.language.currentLanguage(),
        })
      );
      this._state.set({
        currentStep: OnboardingStep.WELCOME,
        completedSteps: [],
        childrenIds: [],
        updatedAt: new Date().toISOString(),
      });
      this._children.set([]);
    } catch (e) {
      this._error.set(e instanceof Error ? e.message : 'Error initializing');
      console.error('initOnboarding failed', e);
    } finally {
      this._saving.set(false);
    }
  }

  /** Add a child and persist to Firestore. Firestore writes happen BEFORE local state updates to avoid sync issues. */
  async addChild(profile: OnboardingChildProfileSection): Promise<string | null> {
    this._saving.set(true);
    this._error.set(null);
    let createdChildId: string | null = null;
    try {
      createdChildId = await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.addChild(uid, profile)
      );

      const state = this._state();
      const childrenIds = [...(state?.childrenIds ?? []), createdChildId];
      await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.updateUserOnboarding(uid, {
          ...state,
          childrenIds,
          currentChildId: createdChildId!,
        })
      );

      // Firestore succeeded — now update local state
      const child: ChildDocument = {
        id: createdChildId,
        name: profile.name,
        birthDate: profile.birthDate,
        gender: profile.gender,
        photoURL: profile.photoURL,
        photoStoragePath: profile.photoStoragePath,
        createdAt: new Date().toISOString(),
      };
      this._children.update((list) => [...list, child]);
      this._state.update((s) =>
        s
          ? { ...s, childrenIds, currentChildId: createdChildId!, updatedAt: new Date().toISOString() }
          : null
      );
      this.childrenData.invalidate();
      return createdChildId;
    } catch (e) {
      this._error.set(e instanceof Error ? e.message : 'Error adding child');
      console.error('addChild failed', e);

      // Rollback: if child was created but user doc update failed, remove orphaned child
      if (createdChildId) {
        try {
          await firestoreRetry(this.auth, (uid) =>
            this.dataAccess.removeChild(uid, createdChildId!)
          );
          if (profile.photoStoragePath) {
            await this.storageUpload.deleteFile(profile.photoStoragePath);
          }
        } catch (rollbackError) {
          console.warn('addChild rollback failed (child may be orphaned):', rollbackError);
        }
      }
      return null;
    } finally {
      this._saving.set(false);
    }
  }

  /** Add first memory for a child. Rolls back orphaned memory doc if child update fails. */
  async addFirstMemory(
    childId: string,
    memory: OnboardingFirstMemoryData
  ): Promise<void> {
    this._saving.set(true);
    this._error.set(null);
    const wasCreate = !memory.id; // Track whether this is a create or update
    let createdMemoryId: string | null = null;
    try {
      const memoryId = await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.createMemoryDoc(uid, childId, memory)
      );
      if (wasCreate) {
        createdMemoryId = memoryId;
      }
      const memoryWithId = { ...memory, id: memoryId };
      await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.addFirstMemoryToChild(uid, childId, memoryWithId)
      );

      this._children.update((list) =>
        list.map((c) =>
          c.id === childId ? { ...c, firstMemory: memoryWithId } : c
        )
      );
    } catch (e) {
      this._error.set(
        e instanceof Error ? e.message : 'Error saving first memory'
      );
      console.error('addFirstMemory failed', e);

      // Rollback: if we created a new memory doc but child update failed, remove it
      if (createdMemoryId) {
        try {
          await firestoreRetry(this.auth, (uid) =>
            this.dataAccess.removeMemory(uid, childId, createdMemoryId!)
          );
        } catch (rollbackError) {
          console.warn('addFirstMemory rollback failed (memory may be orphaned):', rollbackError);
        }
      }
    } finally {
      this._saving.set(false);
    }
  }

  /** Update invite circle invitations */
  async updateInviteCircle(invitations: CircleInvitation[]): Promise<void> {
    this._saving.set(true);
    this._error.set(null);
    try {
      await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.saveInviteCircle(uid, invitations)
      );
      this._state.update((s) =>
        s
          ? {
              ...s,
              inviteCircle: { invitations },
              updatedAt: new Date().toISOString(),
            }
          : null
      );
    } catch (e) {
      this._error.set(
        e instanceof Error ? e.message : 'Error saving invitations'
      );
      console.error('updateInviteCircle failed', e);
    } finally {
      this._saving.set(false);
    }
  }

  /** Complete current step and navigate to next */
  async completeStep(
    step: OnboardingStep,
    options?: { showLoading?: boolean }
  ): Promise<void> {
    const state = this._state();
    if (!state) return;

    const showLoading = options?.showLoading ?? true;
    const completed = [...new Set([...state.completedSteps, step])];
    const nextStep = this.getNextStep(step);
    const isLastStep = !nextStep;

    const updated: UserOnboardingState = {
      ...state,
      completedSteps: completed,
      currentStep: (nextStep ?? OnboardingStep.COMPLETED) as OnboardingStep,
      updatedAt: new Date().toISOString(),
      completedAt: isLastStep ? new Date().toISOString() : state.completedAt ?? null,
    };

    if (showLoading) this._saving.set(true);
    this._error.set(null);
    try {
      await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.updateUserOnboarding(uid, updated)
      );
      this._state.set(updated);

      this.analytics.trackEvent('onboarding_step', {
        step: STEP_ORDER.indexOf(step) + 1,
        name: step,
      });

      if (isLastStep) {
        this.analytics.trackEvent('onboarding_completed', {
          children_count: this._children().length,
        });
        this.router.navigate([ROUTES.HOME]);
      } else {
        this.router.navigate([ROUTES.ONBOARDING.ROOT, nextStep]);
      }
    } catch (e) {
      this._error.set(e instanceof Error ? e.message : 'Error completing step');
      console.error('completeStep failed', e);
    } finally {
      if (showLoading) this._saving.set(false);
    }
  }

  /** Navigate to a specific step (if allowed) */
  goToStep(step: OnboardingStep): void {
    const completed = this.completedSteps();
    const idx = STEP_ORDER.indexOf(step);
    const canAccess =
      idx === 0 || (idx > 0 && completed.includes(STEP_ORDER[idx - 1]));
    if (canAccess) {
      this.router.navigate([ROUTES.ONBOARDING.ROOT, step]);
    }
  }

  /** Remove a child and their photo from Storage. Firestore writes before local state; storage delete is best-effort. */
  async removeChild(childId: string): Promise<void> {
    const child = this._children().find((c) => c.id === childId);

    this._saving.set(true);
    this._error.set(null);
    try {
      // 1. Delete child doc in Firestore (source of truth)
      await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.removeChild(uid, childId)
      );

      // 2. Update user doc childrenIds
      const currentState = this._state();
      const newChildrenIds =
        currentState?.childrenIds.filter((id) => id !== childId) ?? [];
      await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.updateUserOnboarding(uid, { childrenIds: newChildrenIds })
      );

      // 3. Update local signals (Firestore succeeded)
      this._children.update((list) => list.filter((c) => c.id !== childId));
      this._state.update((s) =>
        s
          ? { ...s, childrenIds: newChildrenIds, updatedAt: new Date().toISOString() }
          : null
      );
      this.childrenData.invalidate();

      // 4. Best-effort storage cleanup — don't fail the op if photo delete fails
      if (child?.photoStoragePath) {
        try {
          await this.storageUpload.deleteFile(child.photoStoragePath);
        } catch (storageError) {
          console.warn('removeChild: photo cleanup failed (non-blocking):', storageError);
        }
      }
    } catch (e) {
      this._error.set(e instanceof Error ? e.message : 'Error removing child');
      console.error('removeChild failed', e);
    } finally {
      this._saving.set(false);
    }
  }

  /** Set current child for first-memory step (when cycling through children). Persists to Firestore. */
  async setCurrentChildId(childId: string | undefined): Promise<void> {
    try {
      await firestoreRetry(this.auth, (uid) =>
        this.dataAccess.updateUserOnboarding(uid, { currentChildId: childId })
      );
      this._state.update((s) =>
        s ? { ...s, currentChildId: childId, updatedAt: new Date().toISOString() } : null
      );
    } catch (e) {
      console.error('setCurrentChildId failed', e);
    }
  }

  /**
   * Resolve onboarding navigation target for guards.
   * Delegates to data-access layer, wrapped with firestoreRetry.
   */
  async resolveOnboardingStep(uid: string): Promise<{
    completed: boolean;
    step: string;
    docExists: boolean;
  }> {
    return this.dataAccess.resolveOnboardingStep(uid);
  }

  private getNextStep(current: OnboardingStep): OnboardingStep | null {
    const idx = STEP_ORDER.indexOf(current);
    if (idx === -1 || idx >= STEP_ORDER.length - 1) return null;
    return STEP_ORDER[idx + 1];
  }
}
